import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { useLocation, type Location } from 'react-router';
import i18next from 'i18next';
import { v4 } from 'uuid';
import getUuidFromString from 'uuid-by-string';

import { useCosmos } from '../CosmosContext';
import { getRoute, toTitleCase } from 'utility/functions';
import { AssistantTextProps } from 'components/Assistant/AssistantText/AssistantText';
import { getOmniSearchValue } from 'providers/hooks/useOmniSearch';
import {
  TAssistant,
  TGetIsMenuOpen,
  TInterfaceObject,
  TInterfaceObjectParams,
  TMenu,
  TMenuAppContext,
  TTrackEvent,
} from './analytics';
import { useCosmosLayout } from 'providers/contexts/CosmosLayoutContext';

// ====================================
// Constants.
// ====================================
// All the menu ids to be queried for in the getMenuInfo func.
export const menuIds = {
  global: {
    // Main navbar at the top of the page.
    'Grid Header': {
      id: 'grid-header',
      uuid: getUuidFromString('grid-header'),
    },
    // Bento menu.
    'Grid Header Navigation': {
      id: 'bento-popover-stack-id',
      uuid: getUuidFromString('bento-popover-stack-id'),
    },
    // Plus button for asset creation in main navbar.
    'Grid Header Create Asset': {
      id: 'plus-button-main-grid',
      uuid: getUuidFromString('plus-button-main-grid'),
    },
    // User/profile menu in main navbar.
    'Grid Header Profile': {
      id: 'user-menu-container',
      uuid: getUuidFromString('user-menu-container'),
    },
    // Left sidebar navigation.
    'Grid Sidebar': {
      id: 'grid-sidebar',
      uuid: getUuidFromString('grid-sidebar'),
    },
  },
  editorial: {
    // Editorial navbar with save, preview, publish, etc.
    'Grid Action Bar': {
      id: 'asset-action-bar',
      uuid: getUuidFromString('asset-action-bar'),
    },
    // Editorial left nav sidebar with meta, stats, etc.
    'Editorial Left Bar Content': {
      id: 'left-bar-content',
      uuid: getUuidFromString('left-bar-content'),
    },
    'Editorial Left Bar Metadata': {
      id: 'left-bar-meta',
      uuid: getUuidFromString('left-bar-meta'),
    },
    'Editorial Left Bar Stats': {
      id: 'left-bar-stats',
      uuid: getUuidFromString('left-bar-stats'),
    },
    'Editorial Left Bar Revision': {
      id: 'left-bar-revision',
      uuid: getUuidFromString('left-bar-revision'),
    },
  },
  dashboard: {
    'Dashboard Action Bar': {
      id: 'dashboard-action-bar',
      uuid: getUuidFromString('dashboard-action-bar'),
    },
  },
} as const;

// All the assistant ids to be queried for in the getMenuInfo func.
// Child components of AssistantMain.tsx
const assistantTextIds: AssistantTextProps['name'][] = [
  'titleAssistant',
  'summaryAssistant',
  'seoTitleAssistant',
  'seoDescriptionAssistant',
  'socialTitleAssistant',
  'socialSummaryAssistant',
  'urlTitleAssistant',
];

const assistantIds = {
  'Create Article Asset SEO': {
    id: 'assistant--score-card',
    uuid: getUuidFromString('assistant--score-card'),
  },
  'Keyword Assistant': {
    id: 'assistant--keywords',
    uuid: getUuidFromString('assistant--keywords'),
  },

  // All of the text assistants.
  ...assistantTextIds.reduce(
    (acc, name) => {
      const title = name
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .trim();

      // Create the object to spread into assistantIds.
      acc[toTitleCase(title)] = {
        id: `assistant--text__${name}`,
        uuid: getUuidFromString(`assistant--text__${name}`),
      };

      return acc;
    },
    {} as Record<string, { id: string; uuid: string }>,
  ),
} as const;

// ====================================
// Helpers.
// ====================================
/**
 * Get the app context based on the current location.
 * @param location
 */
export const getAppContext = (location: Location): TMenuAppContext => {
  const { pathname } = location;

  const hasSearch = !!getOmniSearchValue()?.length;
  const pathWithoutActiveSite = getRoute(pathname);
  let context: TMenuAppContext = 'Content Management';

  switch (true) {
    // Search is a special case.
    case hasSearch && pathWithoutActiveSite.includes('search'):
      context = 'Omni Search';
      break;
    case pathWithoutActiveSite.includes('dashboard') ||
      !pathWithoutActiveSite?.length ||
      pathWithoutActiveSite === '/':
      context = 'Dashboards';
      break;
    // Users.
    // Until routes are updated (admin/account) the /edit/ path will always return User Management.
    case pathWithoutActiveSite.includes('user'):
      if (pathWithoutActiveSite.includes('user/admin')) {
        context = 'Admin Management';
        break;
      } else {
        context = 'User Management';
        break;
      }
    case pathWithoutActiveSite.includes('emailreach'):
      context = 'Email Reach';
      break;
    case pathWithoutActiveSite.includes('feeds'):
    case pathWithoutActiveSite.includes('editorial'):
      context = 'Content Management';
      break;
    case pathWithoutActiveSite.includes('services'):
      context = 'Services';
      break;
    case pathWithoutActiveSite.includes('journeys'):
      context = 'Journeys';
      break;
    case pathWithoutActiveSite.includes('design'):
      context = 'Design';
      break;
    case pathWithoutActiveSite.startsWith('settings'):
      context = 'Content Management';
      break;
  }

  return context;
};

const getIncludeEditorialMenus = (location: Location) => {
  if (!location) return false;
  if (
    location.pathname.includes('editorial/edit') ||
    location.pathname.includes('editorial/create')
  ) {
    return true;
  }
};

/**
 * "Assistant Object" (https://townnews.atlassian.net/wiki/spaces/TD/pages/2792816641/Admin+Analytics+Events#Assistant-Object)
 * Track the AI Assistant: id, name, type, is_open.
 * Is included in other tracking calls.
 * @returns array of assistant objects
 */
const getAssistantInfo = (location: Location) => {
  // If not in the asset editor, don't include the editorial assistants.
  if (!getIncludeEditorialMenus(location)) return [];

  const assistantInfo: TAssistant[] = Object.entries(assistantIds).map(
    ([name, { id, uuid }]) => {
      const assistant: HTMLElement | null = document.getElementById(id);

      // If the assistant cannot be found in the dom, manually form the assistant data.
      if (!assistant) {
        return {
          assistant_id: uuid,
          assistant_name: name,
          assistant_app_context: getAppContext(location),
          is_open: false,
        };
      }

      // Check if the assistant is open using the dom.
      const isOpenViaDom = assistant.style.display !== 'none';

      return {
        assistant_id: uuid,
        assistant_name: name,
        assistant_app_context: getAppContext(location),
        is_open: isOpenViaDom,
      };
    },
  );

  return assistantInfo;
};

const checkMenuDataState = async (id: string) => {
  let bentoMenu = document.getElementById(id);
  let isOpen = bentoMenu?.getAttribute('data-state') === 'open';

  await new Promise((resolve) => {
    setTimeout(() => {
      bentoMenu = document.getElementById(id);
      isOpen = bentoMenu?.getAttribute('data-state') === 'open';
      resolve(null);
    }, 400);
  });

  return isOpen;
};

/**
 * Check if a menu is open based on the id and the dom.
 * @param id string
 * @param menu HTMLElement
 * @returns boolean
 */
const getIsMenuOpen = async ({ id, leftNavOpen, location }: TGetIsMenuOpen) => {
  // Main left nav (grid sidebar).
  if (id === 'grid-sidebar') return leftNavOpen;

  // These menus have a data-state attribute, which is used to determine if the element is open.
  const menusWithDataStateAttr = [
    menuIds.global['Grid Header Profile'].id as string,
    menuIds.global['Grid Header Create Asset'].id as string,
  ];
  if (menusWithDataStateAttr.includes(id)) {
    return await checkMenuDataState(id);
  }

  // "Grid Header Navigation" ("bento" menu).
  if (id === 'bento-popover-stack-id') {
    return await checkMenuDataState('bento-popover-id');
  }

  // Check if the new asset/plus button menu is open using the dom.
  if (id === 'plus-button-main-grid') {
    const plusButton = document.getElementById(id);
    const isHidden = plusButton?.style?.visibility === 'hidden';

    return !isHidden;
  }

  // Content Editor - left nav (content, meta, stats, revisions).
  // left-bar-content, left-bar-meta, left-bar-stats
  if (id.startsWith('left-bar-')) {
    // Check if the app context is Content Management.
    // Failsafe for (getMenuInfo.includeEditorialMenus).
    if (
      getAppContext(location) !== 'Content Management' &&
      !location?.pathname?.includes('feeds')
    ) {
      return false;
    }

    // Editorial Left Nav Logic: Content, Meta, Stats, Revisions.
    // Check if the editorial-left-bar-subgrid is displayed and opened->just return false.
    const editorialLeftBar = document.querySelector('[data-testid="editorial-left-bar"]');
    const isEditorialLeftBarClosed =
      editorialLeftBar?.getAttribute('data-state') === 'closed';

    // Break out early if the left bar is closed.
    if (isEditorialLeftBarClosed) return false;

    // If editorialLeftBar is open, check the aria-hidden value of the menu.
    const editorialLeftNavIds = {
      content: 'content-tab-visibility',
      meta: 'metadata-tab-visibility',
      stats: 'stats-tab-visibility',
      revision: 'revisions-tab-visibility',
    };

    const leftBarKey = id.split('-')[2] as 'content' | 'meta' | 'stats' | 'revision';
    const editorialMenu = await document.getElementById(editorialLeftNavIds[leftBarKey]);

    if (!editorialMenu) return false;

    const isEditorialLeftNavOpen = editorialMenu?.getAttribute('data-open') === 'true';

    // Return the opposite of the aria-hidden value.
    return isEditorialLeftNavOpen;
  }

  const menu = document.getElementById(id);
  const isDisplayNone = menu?.style?.display === 'none';

  return !!menu || !isDisplayNone || false;
};

/**
 * Gather the menu objects to track in the interface (https://townnews.atlassian.net/wiki/spaces/TD/pages/2792816641/Admin+Analytics+Events#Menu-Object)
 * @param menuId string
 * @param isOpen boolean
 * @returns array of menu objects
 */
const getMenuInfo = async (
  location: Location,
  leftNavOpen: boolean,
  event: string,
): Promise<TMenu[]> => {
  const appContext = getAppContext(location);
  const includeEditorialMenus = getIncludeEditorialMenus(location) || false;

  const menus = includeEditorialMenus
    ? { ...menuIds.global, ...menuIds.editorial }
    : menuIds.global;

  return await Promise.all(
    Object.entries(menus).map(async ([name, { id, uuid }]) => {
      // Check if the menu is open using the dom.
      const isOpenViaDom =
        (await getIsMenuOpen({ id, leftNavOpen, location, event })) ?? false;

      return {
        menu_id: uuid,
        menu_name: name,
        menu_app_context: appContext,
        is_open: isOpenViaDom,
      };
    }),
  );
};

/**
 * Track the User Interface: color theme, language, active site, ai assistants, menus.
 * "Interface Object" (https://townnews.atlassian.net/wiki/spaces/TD/pages/2792816641/Admin+Analytics+Events#Interface-Object)
 * @returns void
 */
const getInterfaceObject = async ({
  lang = i18next.language || 'en-US',
  activeSite,
  sessionConfig,
  leftNavOpen,
  location,
  event,
}: TInterfaceObjectParams): Promise<TInterfaceObject> => {
  // Using local storage userPrefersTracking (TrackingButton.tsx).
  const isSessionTrackingEnabled =
    JSON?.parse?.(localStorage?.getItem('userPrefersTracking') || 'false') === 'true' ||
    false;

  // Get the light or dark theme--not always stored in local storage (RaStore.theme).
  const storedTheme: 'light' | 'dark' | null = JSON.parse(
    localStorage.getItem('RaStore.theme') || 'null',
  );
  const mainLayoutGrid = document.getElementById('main-layout-grid');
  const isDomDarkTheme =
    !storedTheme?.length && !!mainLayoutGrid
      ? getComputedStyle(mainLayoutGrid as Element)?.getPropertyValue(
          'background-color',
        ) === 'rgb(0, 0, 0)'
      : false;

  return {
    color_theme: !!storedTheme ? storedTheme : isDomDarkTheme ? 'dark' : 'light',
    language: lang,
    session_tracking_enabled: isSessionTrackingEnabled,
    active_site_id: activeSite,
    active_site_name: sessionConfig?.site_name,

    // AI assistants and menus.
    assistants: getAssistantInfo(location) || [],
    menus: (await getMenuInfo(location, leftNavOpen, event)) || [],
  };
};

// ====================================
// Omni Navigation Tracking
// ====================================

/**
 * Navigation state type for OmniSearch analytics tracking
 */
export type TrackNavState = {
  navSource: 'omniSearch' | string;
  omniQuery: string;
};

type CheckOmniNavOriginResult = {
  isFromOmniSearch: boolean;
  omniQuery: string;
};

export function checkOmniNavOrigin(
  state?: TrackNavState | null,
): CheckOmniNavOriginResult {
  const isFromOmniSearch = state?.navSource === 'omniSearch';
  const omniQuery = state?.omniQuery ?? '';

  const result = {
    isFromOmniSearch: isFromOmniSearch,
    omniQuery: omniQuery,
  };
  return result;
}

// ====================================
// Context.
// ====================================
interface AnalyticsProviderProps {
  children?: ReactNode;
}

export type AnalyticsContextProps = {
  trackEvent: ({ event, data, enabled }: TTrackEvent) => Promise<void>;
  menuIds: {
    global: Record<string, { id: string; uuid: string }>;
    editorial: Record<string, { id: string; uuid: string }>;
  };
  trackNav: TrackNavState | null;
  setTrackNav: React.Dispatch<React.SetStateAction<TrackNavState | null>>;
};

export const AnalyticsContext = createContext<AnalyticsContextProps | undefined>(
  undefined,
);

// Hook to use the analytics context.
export const useAnalytics = (): AnalyticsContextProps => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

/**
 * Provider for the Analytics Context, which uses Twilio's Segment SDK.
 * NXT Docs: https://townnews.atlassian.net/wiki/spaces/TD/pages/2792816641/Admin+Analytics+Events#Interface-Object
 * Segment Docs: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/
 * @param children ReactNode
 * @returns context wrapper
 */
export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const { sessionConfig, activeSite } = useCosmos();
  const location = useLocation();
  const { leftNavOpen } = useCosmosLayout();
  const sessionId = useRef(v4()).current;

  // Tracking nav state for OmniSearch analytics tracking...
  // note: tried using RRD state direct but was ephemeral or cleared
  // in certain edge cases when navigating to some search pages due
  // to qp/url changes and component remounts, using react state
  // for better persistence and manual control over state values...
  const [trackNav, setTrackNav] = useState<TrackNavState | null>(null);

  // Initialize Twilio Segment (Analytics) SDK.
  const analytics: AnalyticsBrowser | null = useMemo(() => {
    try {
      const segmentAnalyticsId = sessionConfig?.service_keys?.segment_id;

      if (!segmentAnalyticsId) {
        console.error('Segment Analytics ID is missing.');
        return null;
      }

      return AnalyticsBrowser.load({ writeKey: segmentAnalyticsId });
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
      return null;
    }
  }, [sessionConfig?.service_keys?.segment_id]);

  /**
   * Track the event and send the data to Segment.
   * @param event string
   * @param data info to be tracked aside from the interface object
   * @returns void
   */
  const trackEvent = useCallback(
    async ({ event, data, enabled = true, interfaceOverrides }: TTrackEvent) => {
      if (!analytics || !enabled) {
        return;
      }

      // Form a clean payload.
      const payload = Object.assign({}, data);

      // Exclude the interface object check.
      const excludeInterfaceObjectEventIds = ['Admin Signed In'];
      const shouldExcludeInterfaceObject = excludeInterfaceObjectEventIds.includes(event);

      // Include the interface object. Allow callers to provide overrides which
      // will be merged on top of the computed interface object.
      if (!shouldExcludeInterfaceObject) {
        const iface = await getInterfaceObject({
          activeSite,
          sessionConfig,
          leftNavOpen,
          location,
          event,
        });

        // Merge overrides if provided on the trackEvent call (these should take precedence)
        payload.interface = interfaceOverrides
          ? { ...iface, ...interfaceOverrides }
          : iface;
      }

      // Include the session id.
      payload.session_id = sessionId;

      // Track the event.
      analytics?.track?.(event, payload);
    },
    [activeSite, analytics, leftNavOpen, location, sessionConfig, sessionId],
  );

  // ====================================
  // Hooks.
  // ====================================
  // Admin Page Viewed (https://townnews.atlassian.net/wiki/spaces/TD/pages/2792816641/Admin+Analytics+Events#Admin-Page-Viewed).
  // Ignores asset pages (create/edit) tracker has to wait for the dom to render to track the asset grid properly
  // AssetForm.tsx -> leftBarButtonsRef.
  const prevPath = useRef<string>();

  useEffect(() => {
    const handlePageView = () => {
      const pathWithoutActiveSite = getRoute(location.pathname);
      // These paths have their own "Admin Page Viewed" tracking calls.
      const isolatedPageTrackingPaths = [
        'editorial/create/',
        'editorial/edit/',
        'feeds/view/',
      ];
      const isIsolated = isolatedPageTrackingPaths.some((path) =>
        pathWithoutActiveSite.includes(path),
      );

      if (location.pathname !== prevPath.current && !isIsolated) {
        trackEvent({
          event: 'Admin Page Viewed',
          data: {
            title: document.title,
            url: location.pathname,
            referrer_url: prevPath.current || '',
          },
        });
        prevPath.current = location.pathname;
      }
    };

    if (document.readyState === 'complete') {
      handlePageView();
    } else {
      const onReady = () => {
        if (document.readyState === 'complete') {
          handlePageView();
          document.removeEventListener('readystatechange', onReady);
        }
      };
      document.addEventListener('readystatechange', onReady);
    }

    return () => {
      prevPath.current = location.pathname;
      document.removeEventListener('readystatechange', handlePageView);
    };
  }, [location.pathname, trackEvent]);

  return (
    <AnalyticsContext.Provider
      value={{
        trackEvent,
        menuIds,
        trackNav,
        setTrackNav,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
